/**
 * Consola web Faro (SPA sin framework), la consola de administración del servicio:
 * equipos en vivo, grupos, usuarios/permisos, despliegue, horarios, registros,
 * seguridad (2FA, lista blanca de IP).
 */

type Role = 'owner' | 'admin' | 'member';
interface User { id: string; email: string; name: string; role: Role; totpEnabled: boolean; teamId: string; teamName: string; status?: string; lastLoginAt?: string | null }
interface Group { id: string; name: string; deviceCount?: number }
interface Device {
  id: string; name: string; groupId: string | null; groupName?: string | null; status: 'online' | 'offline' | 'in_session';
  lastSeenAt: string | null; publicIp: string | null; notes: string; createdAt: string;
  info: { hostname: string; os: string; osVersion: string; arch: string; version: string; macs: string[]; lanIps: string[]; monitors: number; loggedUser?: string; cpu?: string; memGb?: number };
  settings: Record<string, unknown>;
  sessions: { sessionId: string; userName: string; mode: string; startedAt: string }[];
}

const $app = document.getElementById('app')!;
let token = localStorage.getItem('faro.token') || '';
let me: User | null = null;
let devices: Device[] = [];
let groups: Group[] = [];
let ws: WebSocket | null = null;
let deviceFilter = { q: '', group: '', status: '' };

// ---------- utilidades ----------

function brand(cls = 'brand'): HTMLElement { return h('div', { class: cls }, h('img', { src: '/brand/faro.svg', alt: '' }), 'Faro'); }
function h(tag: string, attrs: Record<string, unknown> = {}, ...children: (Node | string | null | undefined | false)[]): HTMLElement {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k === 'class') el.className = String(v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
    else if (k === 'html') el.innerHTML = String(v);
    else if (k === 'value') (el as HTMLInputElement).value = String(v);
    else if (k === 'checked') (el as HTMLInputElement).checked = !!v;
    else if (k === 'disabled') (el as HTMLButtonElement).disabled = !!v;
    else el.setAttribute(k, String(v));
  }
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.append(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

function toast(text: string, kind: 'ok' | 'err' | '' = ''): void {
  const t = h('div', { class: `toast ${kind}` }, text);
  document.body.append(t);
  setTimeout(() => t.remove(), 3500);
}

async function api<T = unknown>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    if (res.status === 401 && me) logout();
    const issues = Array.isArray(data.issues) ? `: ${(data.issues as string[]).join(', ')}` : '';
    throw new Error(String(data.error || res.statusText) + issues);
  }
  return data as T;
}

function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
}
function ago(s: string | null | undefined): string {
  if (!s) return 'nunca';
  const diff = Date.now() - new Date(s).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'ahora';
  if (m < 60) return `hace ${m} min`;
  const hrs = Math.floor(m / 60);
  if (hrs < 48) return `hace ${hrs} h`;
  return `hace ${Math.floor(hrs / 24)} d`;
}

function modal(title: string, body: HTMLElement, buttons: { label: string; primary?: boolean; danger?: boolean; onClick: () => Promise<void> | void }[]): () => void {
  const close = () => bg.remove();
  const bg = h('div', { class: 'modal-bg', onClick: (e: Event) => { if (e.target === bg) close(); } },
    h('div', { class: 'modal' }, h('h2', {}, title), body,
      h('div', { class: 'buttons' },
        h('button', { onClick: close }, 'Cancelar'),
        ...buttons.map((b) => h('button', { class: `${b.primary ? 'primary' : ''} ${b.danger ? 'danger' : ''}`, onClick: async () => { try { await b.onClick(); close(); } catch (e) { toast((e as Error).message, 'err'); } } }, b.label)),
      )));
  document.body.append(bg);
  return close;
}

function field(label: string, input: HTMLElement): HTMLElement {
  return h('div', {}, h('label', {}, label), input);
}
function input(attrs: Record<string, unknown>): HTMLInputElement {
  return h('input', attrs) as HTMLInputElement;
}
function select(options: { value: string; label: string }[], value = ''): HTMLSelectElement {
  const s = h('select', {}) as HTMLSelectElement;
  for (const o of options) s.append(h('option', { value: o.value, selected: o.value === value ? 'selected' : undefined }, o.label));
  s.value = value;
  return s;
}

// ---------- auth ----------

function logout(): void {
  token = '';
  me = null;
  localStorage.removeItem('faro.token');
  ws?.close();
  ws = null;
  location.hash = '#/login';
  render();
}

async function boot(): Promise<void> {
  const st = await api<{ needsSetup: boolean }>('GET', '/api/status');
  if (st.needsSetup) {
    location.hash = '#/setup';
    render();
    return;
  }
  if (token) {
    try {
      const r = await api<{ user: User }>('GET', '/api/me');
      me = r.user;
      connectWs();
    } catch {
      token = '';
      localStorage.removeItem('faro.token');
    }
  }
  if (!me && !location.hash.startsWith('#/invite/')) location.hash = '#/login';
  render();
}

function connectWs(): void {
  if (ws) ws.close();
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  ws = new WebSocket(`${proto}://${location.host}/ws/app`);
  ws.onopen = () => { ws!.send(JSON.stringify({ t: 'auth', token, protocol: 1 })); };
  ws.onmessage = (ev) => {
    const msg = JSON.parse(ev.data as string) as Record<string, unknown>;
    switch (msg.t) {
      case 'ready':
        ws!.send(JSON.stringify({ t: 'subscribe_devices' }));
        break;
      case 'devices':
        devices = msg.devices as Device[];
        groups = msg.groups as Group[];
        if (location.hash.startsWith('#/computers') || location.hash === '' || location.hash === '#/') render();
        break;
      case 'device_update': {
        const d = msg.device as Device;
        const i = devices.findIndex((x) => x.id === d.id);
        if (i >= 0) devices[i] = d; else devices.push(d);
        if (location.hash.startsWith('#/computers') || location.hash === '' || location.hash === '#/') render();
        break;
      }
      case 'device_removed':
        devices = devices.filter((d) => d.id !== msg.deviceId);
        render();
        break;
      case 'error':
        if (msg.fatal) logout();
        break;
    }
  };
  ws.onclose = () => { if (me) setTimeout(connectWs, 3000); };
  setInterval(() => { if (ws?.readyState === 1) ws.send(JSON.stringify({ t: 'ping' })); }, 25000);
}

// ---------- vistas de autenticación ----------

function viewSetup(): HTMLElement {
  const teamName = input({ placeholder: 'p. ej. 3lsystems' });
  const name = input({ placeholder: 'Tu nombre' });
  const email = input({ type: 'email', placeholder: 'correo@empresa.es' });
  const password = input({ type: 'password', placeholder: 'Mínimo 8 caracteres' });
  return h('div', { class: 'auth' }, h('div', { class: 'card' },
    brand('brand brand--lg'),
    h('p', { class: 'muted' }, 'Primer arranque: crea tu equipo (organización) y el usuario propietario.'),
    field('Nombre del equipo', teamName), field('Tu nombre', name), field('Correo', email), field('Contraseña', password),
    h('div', { style: 'margin-top:16px' }, h('button', { class: 'primary', style: 'width:100%', onClick: async () => {
      try {
        const r = await api<{ token: string; user: User }>('POST', '/api/setup', { teamName: teamName.value, name: name.value, email: email.value, password: password.value });
        token = r.token; me = r.user; localStorage.setItem('faro.token', token);
        connectWs(); location.hash = '#/deploy'; render();
      } catch (e) { toast((e as Error).message, 'err'); }
    } }, 'Crear equipo')),
  ));
}

function viewLogin(): HTMLElement {
  const email = input({ type: 'email', placeholder: 'correo@empresa.es', autofocus: true });
  const password = input({ type: 'password' });
  const totp = input({ placeholder: '000000', inputmode: 'numeric' });
  const totpField = field('Código 2FA', totp);
  totpField.style.display = 'none';
  const remember = input({ type: 'checkbox' });
  remember.checked = true;
  const doLogin = async () => {
    try {
      const r = await api<{ token: string; user: User }>('POST', '/api/auth/login', { email: email.value, password: password.value, totp: totp.value || undefined, remember: remember.checked });
      token = r.token; me = r.user; localStorage.setItem('faro.token', token);
      connectWs(); location.hash = '#/computers'; render();
    } catch (e) {
      if (/2FA/.test((e as Error).message)) totpField.style.display = '';
      toast((e as Error).message, 'err');
    }
  };
  const form = h('div', { class: 'auth' }, h('div', { class: 'card' },
    brand('brand brand--lg'),
    h('p', { class: 'muted', style: 'text-align:center' }, 'Consola de administración'),
    field('Correo', email), field('Contraseña', password), totpField,
    h('label', { style: 'display:flex;align-items:center;gap:8px;margin-top:12px;font-size:13px' }, remember, 'Mantener la sesión iniciada (30 días)'),
    h('div', { style: 'margin-top:16px' }, h('button', { class: 'primary', style: 'width:100%', onClick: doLogin }, 'Entrar')),
    h('p', { class: 'muted', style: 'text-align:center;margin:14px 0 0;font-size:13px' }, h('a', { href: '/app', target: '_blank' }, 'Descargar Faro Técnico (app del técnico)')),
  ));
  form.addEventListener('keydown', (e) => { if ((e as KeyboardEvent).key === 'Enter') void doLogin(); });
  return form;
}

function viewInvite(tok: string): HTMLElement {
  const name = input({ placeholder: 'Tu nombre' });
  const password = input({ type: 'password', placeholder: 'Mínimo 8 caracteres' });
  return h('div', { class: 'auth' }, h('div', { class: 'card' },
    brand('brand brand--lg'),
    h('p', { class: 'muted' }, 'Has sido invitado. Elige tu contraseña para activar la cuenta.'),
    field('Nombre', name), field('Contraseña', password),
    h('div', { style: 'margin-top:16px' }, h('button', { class: 'primary', style: 'width:100%', onClick: async () => {
      try {
        const r = await api<{ token: string; user: User }>('POST', '/api/auth/accept-invite', { token: tok, name: name.value || undefined, password: password.value });
        token = r.token; me = r.user; localStorage.setItem('faro.token', token);
        connectWs(); location.hash = '#/computers'; render();
      } catch (e) { toast((e as Error).message, 'err'); }
    } }, 'Activar cuenta')),
  ));
}

// ---------- shell ----------

const NAV: { path: string; label: string; roles?: Role[] }[] = [
  { path: '#/computers', label: 'Equipos' },
  { path: '#/groups', label: 'Grupos', roles: ['owner', 'admin'] },
  { path: '#/users', label: 'Usuarios y permisos', roles: ['owner', 'admin'] },
  { path: '#/deploy', label: 'Despliegue', roles: ['owner', 'admin'] },
  { path: '#/app', label: 'Faro Técnico' },
  { path: '#/audit', label: 'Auditoría' },
  { path: '#/settings', label: 'Seguridad y cuenta' },
];

/** Pestañas del menú Auditoría (sesiones para todos; registro y horarios solo para administradores). */
const AUDIT_TABS: { path: string; label: string; roles?: Role[] }[] = [
  { path: '#/sessions', label: 'Sesiones' },
  { path: '#/audit', label: 'Registro de actividad', roles: ['owner', 'admin'] },
  { path: '#/schedules', label: 'Horarios de acceso', roles: ['owner', 'admin'] },
];
function auditSection(active: string, content: HTMLElement): HTMLElement {
  const tabs = AUDIT_TABS.filter((t) => !t.roles || t.roles.includes(me!.role));
  return h('div', {}, h('h2', { style: 'margin-top:0' }, 'Registro de actividad'),
    h('div', { class: 'groupbar', style: 'margin-bottom:16px' }, ...tabs.map((t) => h('a', { href: t.path, class: `chip ${active === t.path ? 'active' : ''}` }, t.label))),
    content);
}

function shell(content: HTMLElement): HTMLElement {
  const raw = location.hash.split('/').slice(0, 2).join('/');
  const current = raw === '#/sessions' || raw === '#/schedules' ? '#/audit' : raw;
  const initials = me!.name.split(/\s+/).map((p) => p[0] ?? '').join('').slice(0, 2).toUpperCase() || '·';
  return h('div', { class: 'shell' },
    h('header', { class: 'topbar' },
      h('div', { class: 'topbar__in' },
        brand(),
        h('nav', { class: 'nav' }, ...NAV.filter((n) => !n.roles || n.roles.includes(me!.role)).map((n) => h('a', { href: n.path, class: current === n.path ? 'active' : '' }, n.label))),
        h('div', { class: 'me' },
          h('span', { class: 'me__who', title: `${me!.name} · ${me!.email}` }, me!.teamName, ' · ', me!.email),
          h('span', { class: `tag role-${me!.role}` }, me!.role),
          h('span', { class: 'avatar', title: me!.name }, initials),
          h('button', { class: 'small', onClick: () => logout() }, 'Salir'),
        ),
      ),
    ),
    h('main', { class: 'main' }, content),
  );
}

// ---------- equipos ----------

function viewComputers(): HTMLElement {
  const q = input({ placeholder: 'Buscar por nombre, usuario, IP…', value: deviceFilter.q, onInput: (e: Event) => { deviceFilter.q = (e.target as HTMLInputElement).value; renderTable(); } });
  const g = select([{ value: '', label: 'Todos los grupos' }, { value: '__none', label: 'Sin grupo' }, ...groups.map((x) => ({ value: x.id, label: x.name }))], deviceFilter.group);
  g.onchange = () => { deviceFilter.group = g.value; renderTable(); };
  const st = select([{ value: '', label: 'Cualquier estado' }, { value: 'online', label: 'En línea' }, { value: 'in_session', label: 'En sesión' }, { value: 'offline', label: 'Desconectado' }], deviceFilter.status);
  st.onchange = () => { deviceFilter.status = st.value; renderTable(); };
  const online = devices.filter((d) => d.status !== 'offline').length;
  const inSession = devices.filter((d) => d.status === 'in_session').length;
  const tableWrap = h('div', {});
  const renderTable = () => {
    const list = devices.filter((d) => {
      const needle = deviceFilter.q.toLowerCase();
      if (needle && !`${d.name} ${d.info.hostname} ${d.info.loggedUser ?? ''} ${d.publicIp ?? ''} ${d.info.lanIps.join(' ')} ${d.notes}`.toLowerCase().includes(needle)) return false;
      if (deviceFilter.group === '__none' ? d.groupId : deviceFilter.group && d.groupId !== deviceFilter.group) return false;
      if (deviceFilter.status && d.status !== deviceFilter.status) return false;
      return true;
    });
    tableWrap.replaceChildren(list.length ? h('table', {},
      h('thead', {}, h('tr', {}, h('th', {}, 'Equipo'), h('th', {}, 'Grupo'), h('th', {}, 'Usuario'), h('th', {}, 'Red'), h('th', {}, 'Visto'), h('th', {}, 'Streamer'), h('th', {}, ''))),
      h('tbody', {}, ...list.map((d) => h('tr', {},
        h('td', {}, h('span', { class: `dot ${d.status}` }), h('b', {}, d.name), d.sessions.length ? h('div', { class: 'muted', style: 'font-size:12px' }, `En sesión: ${d.sessions.map((s) => s.userName).join(', ')}`) : null),
        h('td', {}, d.groupName || h('span', { class: 'muted' }, '—')),
        h('td', {}, d.info.loggedUser || h('span', { class: 'muted' }, '—')),
        h('td', {}, h('div', {}, d.publicIp || '—'), h('div', { class: 'muted', style: 'font-size:12px' }, d.info.lanIps.slice(0, 2).join(', '))),
        h('td', {}, d.status === 'offline' ? ago(d.lastSeenAt) : 'ahora'),
        h('td', {}, h('div', {}, d.info.version || '—'), h('div', { class: 'muted', style: 'font-size:12px' }, `${d.info.os} ${d.info.osVersion}`)),
        h('td', {}, h('div', { class: 'actions' },
          h('button', { class: 'small primary', disabled: d.status === 'offline', onClick: () => { location.href = `faro://connect/${d.id}`; toast('Abriendo la app Faro…'); } }, 'Conectar'),
          h('button', { class: 'small', onClick: () => deviceDetail(d) }, 'Detalles'),
          d.status === 'offline'
            ? h('button', { class: 'small', onClick: async () => { try { const r = await api<{ ok: boolean; results: { helper: string; ok: boolean; error?: string }[] }>('POST', `/api/devices/${d.id}/wake`); toast(r.ok ? 'Paquete mágico enviado' : 'No se pudo enviar', r.ok ? 'ok' : 'err'); } catch (e) { toast((e as Error).message, 'err'); } } }, 'Despertar')
            : h('button', { class: 'small', onClick: () => confirmCommand(d, { type: 'reboot', mode: 'normal' }, `¿Reiniciar ${d.name}?`) }, 'Reiniciar'),
        )),
      ))),
    ) : h('div', { class: 'empty' }, devices.length ? 'Ningún equipo coincide con el filtro' : 'Todavía no hay equipos. Crea un código de despliegue e instala Faro Cliente.'));
  };
  renderTable();
  return h('div', {},
    h('h1', {}, 'Equipos'),
    h('div', { class: 'row', style: 'margin-bottom:14px' },
      h('div', { class: 'stat' }, h('b', {}, String(devices.length)), h('span', {}, 'equipos')),
      h('div', { class: 'stat' }, h('b', {}, String(online)), h('span', {}, 'en línea')),
      h('div', { class: 'stat' }, h('b', {}, String(inSession)), h('span', {}, 'en sesión')),
    ),
    h('div', { class: 'card' }, h('div', { class: 'row', style: 'margin-bottom:12px' }, h('div', { class: 'grow' }, q), h('div', { style: 'width:200px' }, g), h('div', { style: 'width:170px' }, st)), tableWrap),
  );
}

function confirmCommand(d: Device, cmd: Record<string, unknown>, text: string): void {
  modal('Confirmar', h('p', {}, text), [{ label: 'Sí, continuar', primary: true, onClick: async () => {
    const r = await api<{ ok: boolean; error?: string }>('POST', `/api/devices/${d.id}/command`, { cmd });
    toast(r.ok ? 'Comando enviado' : r.error || 'Error', r.ok ? 'ok' : 'err');
  } }]);
}

function deviceDetail(d: Device): void {
  const isAdmin = me!.role !== 'member';
  const name = input({ value: d.name, disabled: !isAdmin });
  const grp = select([{ value: '', label: 'Sin grupo' }, ...groups.map((g) => ({ value: g.id, label: g.name }))], d.groupId ?? '');
  grp.disabled = !isAdmin;
  const notes = h('textarea', { rows: 3, disabled: !isAdmin }, d.notes) as HTMLTextAreaElement;
  const s = d.settings as Record<string, unknown>;
  const body = h('div', {},
    h('dl', { class: 'kv' },
      h('dt', {}, 'Estado'), h('dd', {}, h('span', { class: `dot ${d.status}` }), d.status === 'offline' ? `desconectado (${ago(d.lastSeenAt)})` : d.status === 'in_session' ? 'en sesión' : 'en línea'),
      h('dt', {}, 'Host'), h('dd', {}, d.info.hostname),
      h('dt', {}, 'Sistema'), h('dd', {}, `${d.info.os} ${d.info.osVersion} (${d.info.arch})`),
      h('dt', {}, 'CPU / RAM'), h('dd', {}, `${d.info.cpu ?? '—'} / ${d.info.memGb ? d.info.memGb + ' GB' : '—'}`),
      h('dt', {}, 'Monitores'), h('dd', {}, String(d.info.monitors)),
      h('dt', {}, 'IP pública'), h('dd', {}, d.publicIp ?? '—'),
      h('dt', {}, 'IPs locales'), h('dd', {}, d.info.lanIps.join(', ') || '—'),
      h('dt', {}, 'MAC'), h('dd', {}, d.info.macs.join(', ') || '—'),
      h('dt', {}, 'Alta'), h('dd', {}, fmtDate(d.createdAt)),
      h('dt', {}, 'Seguridad'), h('dd', {}, [
        s.requestPermission !== 'off' ? 'pide permiso' : null, s.securityCodeHash ? 'código de seguridad' : null, s.windowsLogin ? 'login Windows' : null,
        s.blankScreenOnConnect ? 'pantalla en negro' : null, s.lockInputOnConnect ? 'bloquea teclado/ratón' : null, s.idleTimeoutMin ? `inactividad ${s.idleTimeoutMin} min` : null,
      ].filter(Boolean).join(' · ') || 'sin restricciones extra'),
    ),
    field('Nombre', name), field('Grupo', grp), field('Notas', notes),
    isAdmin && d.status !== 'offline' ? h('div', { class: 'row', style: 'margin-top:12px' },
      h('button', { class: 'small', onClick: () => confirmCommand(d, { type: 'lock' }, '¿Bloquear la sesión de Windows del equipo?') }, 'Bloquear'),
      h('button', { class: 'small', onClick: () => confirmCommand(d, { type: 'reboot', mode: 'normal' }, '¿Reiniciar el equipo?') }, 'Reiniciar'),
      h('button', { class: 'small', onClick: () => confirmCommand(d, { type: 'reboot', mode: 'safe' }, '¿Reiniciar en modo seguro con red? Faro Cliente se reconectará al arrancar.') }, 'Modo seguro'),
      h('button', { class: 'small', onClick: () => confirmCommand(d, { type: 'reboot', mode: 'streamer' }, '¿Reiniciar solo Faro Cliente?') }, 'Reiniciar Faro Cliente'),
      h('button', { class: 'small', title: `Instalada: ${d.info.version}`, onClick: () => confirmCommand(d, { type: 'update' }, `¿Actualizar Faro Cliente en ${d.name} a la última versión publicada? El equipo descargará el instalador y se reiniciará el programa.`) }, `Actualizar (v${d.info.version})`),
      h('button', { class: 'small', onClick: () => confirmCommand(d, { type: 'shutdown' }, '¿Apagar el equipo?') }, 'Apagar'),
    ) : null,
  );
  const buttons: Parameters<typeof modal>[2] = [];
  if (isAdmin) {
    buttons.push({ label: 'Eliminar equipo', danger: true, onClick: async () => {
      if (!confirm(`¿Eliminar "${d.name}"? Faro Cliente dejará de poder conectarse.`)) throw new Error('Cancelado');
      await api('DELETE', `/api/devices/${d.id}`);
      toast('Equipo eliminado', 'ok');
    } });
    buttons.push({ label: 'Guardar', primary: true, onClick: async () => {
      await api('PATCH', `/api/devices/${d.id}`, { name: name.value, groupId: grp.value || null, notes: notes.value });
      toast('Guardado', 'ok');
    } });
  }
  modal(d.name, body, buttons);
}

// ---------- grupos ----------

async function viewGroups(): Promise<HTMLElement> {
  const r = await api<{ groups: Group[] }>('GET', '/api/groups');
  groups = r.groups;
  const newName = input({ placeholder: 'Nuevo grupo (p. ej. Cliente ACME)' });
  return h('div', {}, h('h1', {}, 'Grupos de equipos'),
    h('div', { class: 'card' },
      h('div', { class: 'row' }, h('div', { class: 'grow' }, newName), h('button', { class: 'primary', onClick: async () => { try { await api('POST', '/api/groups', { name: newName.value }); render(); } catch (e) { toast((e as Error).message, 'err'); } } }, 'Crear')),
      h('table', { style: 'margin-top:12px' }, h('thead', {}, h('tr', {}, h('th', {}, 'Grupo'), h('th', {}, 'Equipos'), h('th', {}, ''))),
        h('tbody', {}, ...groups.map((g) => h('tr', {}, h('td', {}, g.name), h('td', {}, String(g.deviceCount ?? 0)), h('td', {}, h('div', { class: 'actions' },
          h('button', { class: 'small', onClick: () => { const n = input({ value: g.name }); modal('Renombrar grupo', field('Nombre', n), [{ label: 'Guardar', primary: true, onClick: async () => { await api('PATCH', `/api/groups/${g.id}`, { name: n.value }); render(); } }]); } }, 'Renombrar'),
          h('button', { class: 'small danger', onClick: async () => { if (confirm(`¿Eliminar el grupo "${g.name}"? Los equipos quedarán sin grupo.`)) { await api('DELETE', `/api/groups/${g.id}`); render(); } } }, 'Eliminar'),
        )))))),
      groups.length ? null : h('div', { class: 'empty' }, 'Sin grupos'),
    ));
}

// ---------- usuarios ----------

async function viewUsers(): Promise<HTMLElement> {
  const r = await api<{ users: User[]; access: { id: string; user_id: string; device_id: string | null; group_id: string | null }[]; invites: { token: string; email: string; name: string; role: Role; expiresAt: string; link: string }[] }>('GET', '/api/users');
  const devs = await api<{ devices: Device[]; groups: Group[] }>('GET', '/api/devices');
  devices = devs.devices; groups = devs.groups;
  const createUser = () => {
    const name = input({}); const email = input({ type: 'email' }); const pass = input({ type: 'password', placeholder: 'Contraseña inicial (8+)' });
    const role = select([{ value: 'member', label: 'Miembro (solo equipos asignados)' }, { value: 'admin', label: 'Administrador' }, ...(me!.role === 'owner' ? [{ value: 'owner', label: 'Propietario' }] : [])], 'member');
    modal('Nuevo usuario', h('div', {}, field('Nombre', name), field('Correo', email), field('Rol', role), field('Contraseña', pass)), [{ label: 'Crear', primary: true, onClick: async () => { await api('POST', '/api/users', { name: name.value, email: email.value, role: role.value, password: pass.value }); render(); } }]);
  };
  const invite = () => {
    const name = input({}); const email = input({ type: 'email' });
    const role = select([{ value: 'member', label: 'Miembro' }, { value: 'admin', label: 'Administrador' }], 'member');
    modal('Invitar por enlace', h('div', {}, h('p', { class: 'muted' }, 'Se genera un enlace de 72 h. Envíaselo tú (no se envían correos).'), field('Nombre', name), field('Correo', email), field('Rol', role)),
      [{ label: 'Generar enlace', primary: true, onClick: async () => { const x = await api<{ invite: { link: string } }>('POST', '/api/users/invite', { name: name.value, email: email.value, role: role.value }); await navigator.clipboard.writeText(x.invite.link).catch(() => {}); toast('Enlace copiado al portapapeles', 'ok'); render(); } }]);
  };
  const editUser = (u: User) => {
    const name = input({ value: u.name });
    const role = select([{ value: 'member', label: 'Miembro' }, { value: 'admin', label: 'Administrador' }, { value: 'owner', label: 'Propietario' }], u.role);
    const status = select([{ value: 'active', label: 'Activo' }, { value: 'disabled', label: 'Deshabilitado' }], u.status ?? 'active');
    const pass = input({ type: 'password', placeholder: '(sin cambios)' });
    const resetTotp = input({ type: 'checkbox' });
    modal(`Editar ${u.email}`, h('div', {}, field('Nombre', name), field('Rol', role), field('Estado', status), field('Nueva contraseña', pass), h('div', { class: 'checks' }, h('label', {}, resetTotp, 'Desactivar su 2FA (perdió el móvil)'))),
      [{ label: 'Eliminar', danger: true, onClick: async () => { if (!confirm('¿Eliminar usuario?')) throw new Error('Cancelado'); await api('DELETE', `/api/users/${u.id}`); render(); } },
       { label: 'Guardar', primary: true, onClick: async () => { await api('PATCH', `/api/users/${u.id}`, { name: name.value, role: role.value, status: status.value, password: pass.value || undefined, resetTotp: resetTotp.checked || undefined }); render(); } }]);
  };
  const grantAccess = (u: User) => {
    const kind = select([{ value: 'group', label: 'Grupo' }, { value: 'device', label: 'Equipo' }], 'group');
    const target = h('div', {});
    const draw = () => {
      target.replaceChildren(kind.value === 'group'
        ? select(groups.map((g) => ({ value: g.id, label: g.name })))
        : select(devices.map((d) => ({ value: d.id, label: d.name }))));
    };
    kind.onchange = draw; draw();
    modal(`Dar acceso a ${u.name}`, h('div', {}, field('Tipo', kind), field('Destino', target)), [{ label: 'Conceder', primary: true, onClick: async () => {
      const sel = target.querySelector('select') as HTMLSelectElement;
      await api('POST', `/api/users/${u.id}/access`, kind.value === 'group' ? { groupId: sel.value } : { deviceId: sel.value });
      render();
    } }]);
  };
  const accessLabel = (a: { device_id: string | null; group_id: string | null }) =>
    a.group_id ? `grupo ${groups.find((g) => g.id === a.group_id)?.name ?? '?'}` : `equipo ${devices.find((d) => d.id === a.device_id)?.name ?? '?'}`;
  return h('div', {}, h('h1', {}, 'Usuarios y permisos'),
    h('div', { class: 'card' },
      h('div', { class: 'row', style: 'margin-bottom:10px' }, h('button', { class: 'primary', onClick: createUser }, 'Nuevo usuario'), h('button', { onClick: invite }, 'Invitar por enlace')),
      h('table', {}, h('thead', {}, h('tr', {}, h('th', {}, 'Usuario'), h('th', {}, 'Rol'), h('th', {}, '2FA'), h('th', {}, 'Último acceso'), h('th', {}, 'Acceso a equipos'), h('th', {}, ''))),
        h('tbody', {}, ...r.users.map((u) => h('tr', {},
          h('td', {}, h('b', {}, u.name), h('div', { class: 'muted', style: 'font-size:12px' }, u.email)),
          h('td', {}, h('span', { class: `tag role-${u.role}` }, u.role), u.status === 'disabled' ? h('span', { class: 'tag off', style: 'margin-left:4px' }, 'deshabilitado') : null),
          h('td', {}, u.totpEnabled ? 'sí' : h('span', { class: 'muted' }, 'no')),
          h('td', {}, ago(u.lastLoginAt)),
          h('td', {}, u.role === 'member'
            ? h('div', {}, ...r.access.filter((a) => a.user_id === u.id).map((a) => h('div', { class: 'row', style: 'gap:4px' }, h('span', { class: 'tag' }, accessLabel(a)), h('button', { class: 'small', title: 'Revocar', onClick: async () => { await api('DELETE', `/api/users/${u.id}/access/${a.id}`); render(); } }, '×'))), h('button', { class: 'small', style: 'margin-top:4px', onClick: () => grantAccess(u) }, '+ acceso'))
            : h('span', { class: 'muted' }, 'todos')),
          h('td', {}, h('button', { class: 'small', onClick: () => editUser(u) }, 'Editar')),
        )))),
    ),
    r.invites.length ? h('div', { class: 'card' }, h('h2', {}, 'Invitaciones pendientes'), h('table', {}, h('tbody', {}, ...r.invites.map((i) => h('tr', {}, h('td', {}, i.email, ' ', h('span', { class: 'tag' }, i.role)), h('td', {}, 'caduca ', fmtDate(i.expiresAt)), h('td', {}, h('div', { class: 'actions' }, h('button', { class: 'small', onClick: () => { navigator.clipboard.writeText(i.link); toast('Enlace copiado', 'ok'); } }, 'Copiar enlace'), h('button', { class: 'small danger', onClick: async () => { await api('DELETE', `/api/users/invite/${i.token}`); render(); } }, 'Anular')))))))) : null,
  );
}

// ---------- despliegue ----------

interface Installer { platform: string; fileName: string; size: number; version: string | null; uploadedAt: string; uploadedBy: string }
interface PlatformInfo { platform: string; label: string; ext: string }

function fmtBytes(n: number): string {
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function copyText(text: string, msg: string): void {
  navigator.clipboard.writeText(text);
  toast(msg, 'ok');
}

// ---------- despliegue del Streamer (códigos e instalador personalizado) ----------

async function viewDeploy(): Promise<HTMLElement> {
  const r = await api<{ codes: { code: string; name: string; groupId: string | null; createdAt: string; uses: number; enabled: boolean; defaultSettings: Record<string, unknown> }[]; publicUrl: string }>('GET', '/api/deploy-codes');
  const gr = await api<{ groups: Group[] }>('GET', '/api/groups');
  const inst = await api<{ installers: Installer[]; platforms: PlatformInfo[] }>('GET', '/api/installers');
  groups = gr.groups;
  const create = () => {
    const name = input({ placeholder: 'p. ej. Clientes 2026' });
    const grp = select([{ value: '', label: 'Sin grupo' }, ...groups.map((g) => ({ value: g.id, label: g.name }))], '');
    const checks: Record<string, HTMLInputElement> = {};
    const chk = (key: string, label: string, def = false) => { checks[key] = input({ type: 'checkbox', checked: def }); return h('label', {}, checks[key], label); };
    const reqPerm = select([{ value: 'off', label: 'No pedir permiso' }, { value: 'on_reject', label: 'Pedir permiso (rechazar si no contesta)' }, { value: 'on_accept', label: 'Pedir permiso (aceptar si no contesta)' }], 'off');
    const idle = input({ type: 'number', value: '0', min: 0 });
    modal('Nuevo código de despliegue', h('div', {},
      h('p', { class: 'muted' }, 'Los equipos instalados con este código se dan de alta en el grupo indicado y con estos ajustes por defecto (se pueden cambiar luego en cada equipo).'),
      field('Nombre', name), field('Grupo', grp), field('Permiso del usuario local', reqPerm), field('Cortar sesión por inactividad (min, 0 = nunca)', idle),
      h('div', { class: 'checks' }, chk('blankScreenOnConnect', 'Pantalla en negro al conectar'), chk('lockInputOnConnect', 'Bloquear teclado y ratón locales al conectar'), chk('lockOnDisconnect', 'Bloquear Windows al desconectar'), chk('allowFileTransfer', 'Permitir transferencia de ficheros', true), chk('allowChat', 'Permitir chat', true), chk('allowAudio', 'Permitir audio remoto', true), chk('allowClipboard', 'Permitir portapapeles compartido', true), chk('allowRemoteCommands', 'Permitir comandos remotos (reinicio, bloqueo…)', true), chk('lockSettings', 'Bloquear ajustes de Faro Cliente (solo admins)', true)),
    ), [{ label: 'Crear', primary: true, onClick: async () => {
      const ds: Record<string, unknown> = { requestPermission: reqPerm.value, idleTimeoutMin: Number(idle.value || 0) };
      for (const [k, el] of Object.entries(checks)) ds[k] = el.checked;
      await api('POST', '/api/deploy-codes', { name: name.value, groupId: grp.value || null, defaultSettings: ds });
      render();
    } }]);
  };
  const shortLabel: Record<string, string> = { windows: 'Windows (64 bits)', macos: 'macOS', 'linux-deb': 'Ubuntu / Debian (.deb)', 'linux-appimage': 'Linux (AppImage)' };
  const available = inst.platforms.filter((p) => inst.installers.some((i) => i.platform === p.platform));

  /** Desplegable por sistema: al elegir uno aparece el enlace de descarga del instalador con el código incorporado. */
  const installerPicker = (code: string): HTMLElement => {
    const box = h('div', { class: 'linkbox' });
    const sel = select([
      { value: '', label: 'Elige sistema…' },
      ...available.map((p) => ({ value: p.platform, label: shortLabel[p.platform] ?? p.label })),
      { value: '__page', label: 'Página para el cliente (todos los sistemas)' },
    ], '');
    sel.classList.add('small');
    sel.addEventListener('change', () => {
      const v = sel.value;
      if (!v) { box.replaceChildren(); return; }
      const url = v === '__page' ? `${r.publicUrl}/d/${code}` : `${r.publicUrl}/d/${code}/${v}`;
      const meta = inst.installers.find((i) => i.platform === v);
      box.replaceChildren(
        h('code', { class: 'url', title: url }, url),
        h('div', { class: 'actions' },
          h('button', { class: 'small', onClick: () => copyText(url, 'Enlace copiado') }, 'Copiar'),
          v === '__page'
            ? h('button', { class: 'small', onClick: () => window.open(url, '_blank') }, 'Abrir')
            : h('button', { class: 'small primary', onClick: () => { window.location.href = url; } }, 'Descargar'),
          meta ? h('span', { class: 'muted', style: 'font-size:12px' }, `v${meta.version ?? '?'} · ${fmtBytes(meta.size)}`) : null,
        ),
      );
    });
    return h('div', { class: 'picker' }, sel, box);
  };

  return h('div', {}, h('h1', {}, 'Despliegue de Faro Cliente'),
    h('div', { class: 'card' },
      h('div', { class: 'row', style: 'justify-content:space-between;align-items:center' },
        h('h2', { style: 'margin:0' }, 'Códigos de despliegue'),
        h('button', { class: 'primary', onClick: create }, 'Nuevo código')),
      h('p', { class: 'muted' }, 'Un código por cliente o grupo. Elige el sistema del equipo y descarga (o copia) el instalador personalizado: lleva el servidor y el código incorporados, así que el usuario no tiene que escribir nada.'),
      !available.length ? h('p', { class: 'muted' }, 'Todavía no hay instaladores de Faro Cliente publicados en el servidor.') : null,
      h('table', { style: 'margin-top:12px' }, h('thead', {}, h('tr', {}, h('th', {}, 'Código'), h('th', {}, 'Nombre'), h('th', {}, 'Grupo'), h('th', {}, 'Usos'), h('th', {}, 'Instalador personalizado'), h('th', {}, ''))),
        h('tbody', {}, ...r.codes.map((c) => h('tr', {},
          h('td', {}, h('code', {}, c.code), !c.enabled ? h('span', { class: 'tag off', style: 'margin-left:6px' }, 'desactivado') : null),
          h('td', {}, c.name), h('td', {}, groups.find((g) => g.id === c.groupId)?.name ?? '—'), h('td', {}, String(c.uses)),
          h('td', {}, c.enabled && available.length ? installerPicker(c.code) : h('span', { class: 'muted' }, '—')),
          h('td', {}, h('div', { class: 'actions' },
            h('button', { class: 'small', onClick: () => copyText(c.code, 'Código copiado') }, 'Copiar código'),
            h('button', { class: 'small', onClick: async () => { await api('PATCH', `/api/deploy-codes/${c.code}`, { enabled: !c.enabled }); render(); } }, c.enabled ? 'Desactivar' : 'Activar'),
            h('button', { class: 'small danger', onClick: async () => { if (confirm('¿Eliminar código? Los equipos ya registrados no se ven afectados; sus enlaces de descarga dejarán de funcionar.')) { await api('DELETE', `/api/deploy-codes/${c.code}`); render(); } } }, 'Eliminar'),
          )))))),
      r.codes.length ? null : h('div', { class: 'empty' }, 'Crea tu primer código para instalar Faro Cliente en los equipos'),
    ));
}

// ---------- Faro Técnico (descargas de la app) ----------

async function viewApp(): Promise<HTMLElement> {
  const inst = await api<{ installers: Installer[]; platforms: PlatformInfo[] }>('GET', '/api/installers?kind=app');
  const r = await api<{ publicUrl: string }>('GET', '/api/deploy-codes').catch(() => ({ publicUrl: location.origin }));
  const pageUrl = `${r.publicUrl}/app`;
  const rows = inst.platforms.map((p) => {
    const i = inst.installers.find((x) => x.platform === p.platform);
    return h('tr', {},
      h('td', {}, h('b', {}, p.label)),
      h('td', {}, i ? h('span', {}, i.fileName, h('div', { class: 'muted', style: 'font-size:12px' }, `v${i.version ?? '?'} · ${fmtBytes(i.size)} · ${fmtDate(i.uploadedAt)}`)) : h('span', { class: 'muted' }, 'No publicado')),
      h('td', {}, i ? h('div', { class: 'actions' },
        h('button', { class: 'small primary', onClick: () => { window.location.href = `/app/${p.platform}`; } }, 'Descargar'),
        h('button', { class: 'small', onClick: () => copyText(`${r.publicUrl}/app/${p.platform}`, 'Enlace copiado') }, 'Copiar enlace')) : null),
    );
  });
  return h('div', {}, h('h1', {}, 'Faro Técnico'),
    h('div', { class: 'card' },
      h('p', { class: 'muted' }, 'La aplicación con la que el equipo técnico se conecta a los equipos. Página pública de descarga: ',
        h('a', { href: pageUrl, target: '_blank' }, pageUrl), ' ',
        h('button', { class: 'small', onClick: () => copyText(pageUrl, 'Enlace copiado') }, 'Copiar')),
      h('table', {}, h('thead', {}, h('tr', {}, h('th', {}, 'Sistema'), h('th', {}, 'Fichero'), h('th', {}, ''))), h('tbody', {}, ...rows)),
    ));
}

// ---------- horarios ----------

async function viewSchedules(): Promise<HTMLElement> {
  const r = await api<{ schedules: { id: string; name: string; userId: string | null; groupId: string | null; deviceId: string | null; days: number[]; startTime: string; endTime: string; tz: string; enabled: boolean }[] }>('GET', '/api/schedules');
  const us = await api<{ users: User[] }>('GET', '/api/users');
  const dv = await api<{ devices: Device[]; groups: Group[] }>('GET', '/api/devices');
  devices = dv.devices; groups = dv.groups;
  const DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];
  const edit = (s?: typeof r.schedules[number]) => {
    const name = input({ value: s?.name ?? '' });
    const user = select([{ value: '', label: 'Todos los miembros' }, ...us.users.filter((u) => u.role === 'member').map((u) => ({ value: u.id, label: u.name }))], s?.userId ?? '');
    const scope = select([{ value: '', label: 'Todos los equipos' }, ...groups.map((g) => ({ value: `g:${g.id}`, label: `Grupo ${g.name}` })), ...devices.map((d) => ({ value: `d:${d.id}`, label: d.name }))], s?.groupId ? `g:${s.groupId}` : s?.deviceId ? `d:${s.deviceId}` : '');
    const dayChecks = DAYS.map((d, i) => { const c = input({ type: 'checkbox', checked: s ? s.days.includes(i + 1) : i < 5 }); return h('label', { style: 'display:inline-flex;margin-right:8px' }, c, d); });
    const start = input({ type: 'time', value: s?.startTime ?? '08:00' });
    const end = input({ type: 'time', value: s?.endTime ?? '19:00' });
    const enabled = input({ type: 'checkbox', checked: s ? s.enabled : true });
    modal(s ? 'Editar horario' : 'Nuevo horario', h('div', {},
      h('p', { class: 'muted' }, 'Los horarios solo afectan a usuarios con rol miembro. Si un miembro tiene horarios aplicables, solo podrá conectar dentro de ellos.'),
      field('Nombre', name), field('Usuario', user), field('Equipos', scope), h('label', {}, 'Días'), h('div', { class: 'checks' }, h('div', {}, ...dayChecks)),
      h('div', { class: 'grid2' }, field('Desde', start), field('Hasta', end)), h('div', { class: 'checks' }, h('label', {}, enabled, 'Activo')),
    ), [{ label: 'Guardar', primary: true, onClick: async () => {
      const body = { name: name.value, userId: user.value || null, groupId: scope.value.startsWith('g:') ? scope.value.slice(2) : null, deviceId: scope.value.startsWith('d:') ? scope.value.slice(2) : null, days: dayChecks.map((l, i) => ((l.firstChild as HTMLInputElement).checked ? i + 1 : 0)).filter(Boolean), startTime: start.value, endTime: end.value, tz: 'Europe/Madrid', enabled: enabled.checked };
      if (s) await api('PUT', `/api/schedules/${s.id}`, body); else await api('POST', '/api/schedules', body);
      render();
    } }]);
  };
  return h('div', {}, h('h2', { style: 'margin-top:0' }, 'Horarios de acceso'),
    h('div', { class: 'card' }, h('button', { class: 'primary', onClick: () => edit() }, 'Nuevo horario'),
      h('table', { style: 'margin-top:12px' }, h('thead', {}, h('tr', {}, h('th', {}, 'Nombre'), h('th', {}, 'Usuario'), h('th', {}, 'Equipos'), h('th', {}, 'Días'), h('th', {}, 'Horario'), h('th', {}, ''))),
        h('tbody', {}, ...r.schedules.map((s) => h('tr', {},
          h('td', {}, s.name, !s.enabled ? h('span', { class: 'tag off', style: 'margin-left:6px' }, 'inactivo') : null),
          h('td', {}, us.users.find((u) => u.id === s.userId)?.name ?? 'todos los miembros'),
          h('td', {}, s.groupId ? `grupo ${groups.find((g) => g.id === s.groupId)?.name ?? '?'}` : s.deviceId ? devices.find((d) => d.id === s.deviceId)?.name ?? '?' : 'todos'),
          h('td', {}, s.days.map((d) => DAYS[d - 1]).join(' ')), h('td', {}, `${s.startTime} – ${s.endTime}`),
          h('td', {}, h('div', { class: 'actions' }, h('button', { class: 'small', onClick: () => edit(s) }, 'Editar'), h('button', { class: 'small danger', onClick: async () => { if (confirm('¿Eliminar horario?')) { await api('DELETE', `/api/schedules/${s.id}`); render(); } } }, 'Eliminar'))),
        )))),
      r.schedules.length ? null : h('div', { class: 'empty' }, 'Sin horarios: los miembros pueden conectar a cualquier hora a los equipos asignados'),
    ));
}

// ---------- registros ----------

async function viewSessions(): Promise<HTMLElement> {
  const r = await api<{ sessions: { id: string; device_name: string; user_name: string; mode: string; started_at: string; ended_at: string | null; end_reason: string | null; client_ip: string | null }[] }>('GET', '/api/sessions?limit=300');
  const dur = (a: string, b: string | null) => { if (!b) return 'en curso'; const s = Math.round((new Date(b).getTime() - new Date(a).getTime()) / 1000); return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min`; };
  return h('div', {}, h('h2', { style: 'margin-top:0' }, 'Sesiones'),
    h('div', { class: 'card' }, h('table', {}, h('thead', {}, h('tr', {}, h('th', {}, 'Inicio'), h('th', {}, 'Equipo'), h('th', {}, 'Técnico'), h('th', {}, 'Modo'), h('th', {}, 'Duración'), h('th', {}, 'Fin'), h('th', {}, 'IP'))),
      h('tbody', {}, ...r.sessions.map((s) => h('tr', {}, h('td', {}, fmtDate(s.started_at)), h('td', {}, s.device_name), h('td', {}, s.user_name), h('td', {}, h('span', { class: 'tag' }, s.mode)), h('td', {}, dur(s.started_at, s.ended_at)), h('td', { class: 'muted' }, s.end_reason ?? ''), h('td', { class: 'muted' }, s.client_ip ?? ''))))),
      r.sessions.length ? null : h('div', { class: 'empty' }, 'Sin sesiones todavía')));
}

async function viewAudit(): Promise<HTMLElement> {
  const r = await api<{ entries: { id: number; ts: string; actor_type: string; actor_name: string | null; action: string; target: string | null; details: string | null; ip: string | null }[] }>('GET', '/api/audit?limit=400');
  return h('div', {}, h('h1', {}, 'Auditoría'),
    h('div', { class: 'card' }, h('table', {}, h('thead', {}, h('tr', {}, h('th', {}, 'Fecha'), h('th', {}, 'Actor'), h('th', {}, 'Acción'), h('th', {}, 'Detalles'), h('th', {}, 'IP'))),
      h('tbody', {}, ...r.entries.map((e) => h('tr', {}, h('td', {}, fmtDate(e.ts)), h('td', {}, e.actor_name ?? e.actor_type), h('td', {}, h('code', {}, e.action)), h('td', { class: 'muted', style: 'max-width:420px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap' }, e.details ?? ''), h('td', { class: 'muted' }, e.ip ?? '')))))));
}

// ---------- ajustes ----------

async function viewSettings(): Promise<HTMLElement> {
  const meNow = (await api<{ user: User }>('GET', '/api/me')).user;
  me = meNow;
  const cur = input({ type: 'password' }); const np = input({ type: 'password' });
  const totpCard = h('div', { class: 'card' }, h('h2', { style: 'margin-top:0' }, 'Verificación en dos pasos (2FA)'));
  if (meNow.totpEnabled) {
    const pw = input({ type: 'password', placeholder: 'Contraseña para confirmar' });
    totpCard.append(h('p', {}, '2FA ', h('span', { class: 'tag' }, 'activado')), field('Contraseña', pw), h('button', { class: 'danger', style: 'margin-top:8px', onClick: async () => { try { await api('POST', '/api/me/totp/disable', { password: pw.value }); toast('2FA desactivado', 'ok'); render(); } catch (e) { toast((e as Error).message, 'err'); } } }, 'Desactivar 2FA'));
  } else {
    const box = h('div', {});
    totpCard.append(h('p', { class: 'muted' }, 'Añade un código de un solo uso (Google Authenticator, 1Password, Authy…) al inicio de sesión.'), box,
      h('button', { class: 'primary', onClick: async () => {
        const r = await api<{ secret: string; uri: string }>('POST', '/api/me/totp/setup');
        const code = input({ placeholder: '000000' });
        box.replaceChildren(h('p', {}, 'Añade esta clave en tu app de autenticación (o escanea el URI como QR):'), h('pre', {}, r.secret), h('pre', { style: 'white-space:pre-wrap;word-break:break-all' }, r.uri), field('Código generado', code),
          h('button', { class: 'primary', style: 'margin-top:8px', onClick: async () => { try { await api('POST', '/api/me/totp/enable', { code: code.value }); toast('2FA activado', 'ok'); render(); } catch (e) { toast((e as Error).message, 'err'); } } }, 'Confirmar y activar'));
      } }, 'Configurar 2FA'));
  }
  const ipCard = h('div', { class: 'card' }, h('h2', { style: 'margin-top:0' }, 'Lista blanca de IP (consola y app)'));
  if (meNow.role !== 'member') {
    const r = await api<{ entries: { id: string; cidr: string; note: string }[] }>('GET', '/api/ip-allowlist');
    const cidr = input({ placeholder: '203.0.113.0/24 o 2001:db8::/32' }); const note = input({ placeholder: 'Nota' });
    ipCard.append(h('p', { class: 'muted' }, 'Si hay entradas, solo se podrá iniciar sesión desde esas redes. ¡Añade primero tu IP actual para no bloquearte!'),
      h('table', {}, h('tbody', {}, ...r.entries.map((e) => h('tr', {}, h('td', {}, h('code', {}, e.cidr)), h('td', {}, e.note), h('td', {}, meNow.role === 'owner' ? h('button', { class: 'small danger', onClick: async () => { await api('DELETE', `/api/ip-allowlist/${e.id}`); render(); } }, 'Quitar') : null))))),
      meNow.role === 'owner' ? h('div', { class: 'row', style: 'margin-top:10px' }, h('div', { class: 'grow' }, cidr), h('div', { class: 'grow' }, note), h('button', { onClick: async () => { try { await api('POST', '/api/ip-allowlist', { cidr: cidr.value, note: note.value }); render(); } catch (e) { toast((e as Error).message, 'err'); } } }, 'Añadir')) : null);
  }
  const teamCard = h('div', { class: 'card' }, h('h2', { style: 'margin-top:0' }, 'Equipo (organización)'));
  if (meNow.role === 'owner') {
    const t = await api<{ team: { name: string } }>('GET', '/api/team');
    const tn = input({ value: t.team.name });
    teamCard.append(field('Nombre', tn), h('button', { style: 'margin-top:8px', onClick: async () => { await api('PATCH', '/api/team', { name: tn.value }); toast('Guardado', 'ok'); me!.teamName = tn.value; render(); } }, 'Guardar'));
  } else teamCard.append(h('p', { class: 'muted' }, meNow.teamName));
  return h('div', {}, h('h1', {}, 'Seguridad y cuenta'),
    h('div', { class: 'grid2' },
      h('div', { class: 'card' }, h('h2', { style: 'margin-top:0' }, 'Cambiar contraseña'), field('Actual', cur), field('Nueva (8+)', np), h('button', { style: 'margin-top:8px', onClick: async () => { try { await api('POST', '/api/me/password', { current: cur.value, password: np.value }); toast('Contraseña cambiada', 'ok'); cur.value = ''; np.value = ''; } catch (e) { toast((e as Error).message, 'err'); } } }, 'Cambiar')),
      totpCard, ipCard, teamCard,
    ));
}

// ---------- router ----------

let renderSeq = 0;
async function render(): Promise<void> {
  const seq = ++renderSeq;
  const hash = location.hash || '#/computers';
  const [, route, param] = hash.split('/');
  if (route === 'setup') { $app.replaceChildren(viewSetup()); return; }
  if (route === 'invite' && param) { $app.replaceChildren(viewInvite(param)); return; }
  if (!me) { $app.replaceChildren(viewLogin()); return; }
  let content: HTMLElement;
  try {
    switch (route) {
      case 'groups': content = await viewGroups(); break;
      case 'users': content = await viewUsers(); break;
      case 'deploy': content = await viewDeploy(); break;
      case 'app': content = await viewApp(); break;
      case 'schedules': content = me.role === 'member' ? auditSection('#/sessions', await viewSessions()) : auditSection('#/schedules', await viewSchedules()); break;
      case 'sessions': content = auditSection('#/sessions', await viewSessions()); break;
      case 'audit': content = me.role === 'member' ? auditSection('#/sessions', await viewSessions()) : auditSection('#/audit', await viewAudit()); break;
      case 'settings': content = await viewSettings(); break;
      default: content = viewComputers();
    }
  } catch (e) {
    content = h('div', { class: 'card' }, h('b', {}, 'Error: '), (e as Error).message);
  }
  if (seq !== renderSeq) return;
  $app.replaceChildren(shell(content));
}

window.addEventListener('hashchange', () => void render());
void boot();
